import { useEffect, useState } from "react";

/**
 * Загрузка аватарки через fetch под ГЛОБАЛЬНЫМ семафором параллелизма.
 * Сетка «Активность» рисует сотни аватарок разом — если каждая дёрнет
 * `<img>` одновременно, бэкенд-эндпоинт /avatar упрётся в rate-limit Bot API
 * (getUserProfilePhotos→getFile) и отдаст 503 → аватарки «битые». Семафор
 * держит не больше MAX_CONCURRENT запросов одновременно; успешные попадают в
 * R2-кэш, дальше грузятся мгновенно. 503 — транзиентный, ретраим с backoff;
 * 404 — у человека нет фото, показываем букву без ретрая.
 */
const MAX_CONCURRENT = 5;
let active = 0;
const queue: Array<() => void> = [];

function acquire(): Promise<void> {
  if (active < MAX_CONCURRENT) {
    active += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => queue.push(resolve));
}

function release(): void {
  const next = queue.shift();
  if (next) next();
  else active -= 1;
}

export interface AvatarSrc {
  src: string; // objectURL, если фото загружено; иначе ""
  failed: boolean; // true → показать инициал-фолбэк
}

export function useAvatarSrc(url: string | undefined): AvatarSrc {
  const [state, setState] = useState<AvatarSrc>({ src: "", failed: !url });

  useEffect(() => {
    if (!url) {
      setState({ src: "", failed: true });
      return;
    }
    let cancelled = false;
    let objectUrl = "";
    setState({ src: "", failed: false });
    const MAX_RETRY = 4;

    void (async () => {
      for (let attempt = 0; attempt <= MAX_RETRY; attempt += 1) {
        await acquire();
        try {
          if (cancelled) return;
          const res = await fetch(url);
          if (res.ok) {
            const blob = await res.blob();
            if (cancelled) return;
            objectUrl = URL.createObjectURL(blob);
            setState({ src: objectUrl, failed: false });
            return;
          }
          if (res.status === 404) {
            if (!cancelled) setState({ src: "", failed: true }); // нет фото — буква
            return;
          }
          // 503/5xx — лимит Bot API: отпускаем слот и повторяем ниже
        } catch {
          /* сетевой сбой — повторяем */
        } finally {
          release();
        }
        if (attempt < MAX_RETRY && !cancelled) {
          const delay = 500 * Math.pow(1.8, attempt) + Math.random() * 400;
          await new Promise((r) => setTimeout(r, delay));
        }
      }
      if (!cancelled) setState({ src: "", failed: true });
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return state;
}
